// v2.19 (owner 2026-10-25): the shorter ACC & Mockup page. The server decides each row's tab (`tahap`)
// and who may read the list; these helpers only search, filter, count and sort what it sent.
// No imports, so Jest can test them.

const kecil = (s) => String(s || "").toLowerCase();

// search by customer, SPK number or nota number ("144" finds SPK-2026-000144)
export function cocok(r, cari) {
  const q = kecil(cari).trim();
  if (!q) return true;
  return [r.customer, r.nomor_faktur, ...(r.nomor_spks?.length ? r.nomor_spks : [r.nomor_spk])].some((x) => kecil(x).includes(q));
}

export function saring(rows, { cari = "", jenis = "", cs = "" } = {}) {
  return (rows || []).filter((r) => (!jenis || r.jenis === jenis) && (!cs || r.cs_id === cs) && cocok(r, cari));
}

export function hitung(rows, tahap) {
  const n = Object.fromEntries((tahap || []).map((t) => [t.kode, 0]));
  for (const r of rows || []) n[r.tahap] = (n[r.tahap] || 0) + 1;
  return n;
}

// the Kasir/CS accounts present in the list, by name
export function daftarCs(rows) {
  const m = new Map();
  for (const r of rows || []) if (r.cs_id) m.set(r.cs_id, r.cs_nama || "-");
  return [...m.entries()].map(([id, nama]) => ({ id, nama })).sort((a, b) => a.nama.localeCompare(b.nama, "id"));
}

// planned finish as a sortable text; no plan yet = last
export const kunciRencana = (r) => (r.rencana_selesai ? `${String(r.rencana_selesai).slice(0, 10)} ${r.rencana_selesai_jam || "99:99"}` : "~");

export const URUTAN = [["TUNGGU", "Paling lama menunggu"], ["RENCANA", "Rencana selesai terdekat"]];

export function urutkan(rows, mode = "TUNGGU") {
  const kunci = mode === "RENCANA" ? (r) => `${kunciRencana(r)}|${r.menunggu_sejak || ""}` : (r) => String(r.menunggu_sejak || "");
  return [...(rows || [])].sort((a, b) => (kunci(a) < kunci(b) ? -1 : kunci(a) > kunci(b) ? 1 : 0));
}

// "3 hari 2 jam", "5 jam", "12 menit" since an ISO time
export function lamaTunggu(iso, sekarangMs = Date.now()) {
  const t = Date.parse(iso || "");
  if (Number.isNaN(t)) return "";
  const menit = Math.max(0, Math.floor((sekarangMs - t) / 60000));
  const hari = Math.floor(menit / 1440), jam = Math.floor((menit % 1440) / 60);
  if (hari) return jam ? `${hari} hari ${jam} jam` : `${hari} hari`;
  if (jam) return `${jam} jam`;
  return `${menit} menit`;
}

// one short status line per row
export function statusTeks(r) {
  switch (r.tahap) {
    case "BARU": return "Belum ada mockup";
    case "REFERENSI": return `${r.referensi_jumlah} gambar referensi, belum ada mockup`;
    case "MOCKUP": {  // v2.21.1: the version the customer sees (v1.0, v1.1, ...), not the upload count
      const v = r.mockup_kirim_versi ?? r.mockup_versi;
      return `Mockup ${r.label_versi?.[String(v)] || `#${v}`} dikirim, menunggu customer`;
    }
    case "REVISI": return r.draf ? "Revisi dikerjakan, belum dikirim" : "Customer minta revisi";
    case "REVISI_H1": return r.draf ? "Revisi H+1 dikerjakan, belum dikirim" : "Customer minta revisi (hari berikutnya)";
    case "REVISI_SIAP": return `Revisi ke-${r.revisi_siap?.revisi_ke ?? r.revisi_ke} selesai, kirim ke customer`;
    case "PERLU_IZIN": return `Revisi ke-${r.revisi_ke} menunggu izin Owner/Manager`;
    case "DISETUJUI":
      if (r.produksi_jalan) return "Disetujui customer, masuk produksi";
      return r.jenis === "JERSEY" ? "Disetujui customer, tunggu ACC kasir (checklist)" : "Disetujui customer";
    default: return r.tahap || "";
  }
}

export const revisiTeks = (r) => (r.revisi_ke ? `ke-${r.revisi_ke} dari ${r.batas_revisi}` : "belum ada");

// one upload for the staff (v2.20): its number and, once sent, the customer's version; a draft replaced before
// Revisi selesai was never sent (server `GET /transaksi/{id}/mockup`)
export function namaUnggahan(v) {
  if (!v) return "";
  if (v.label_versi) return `#${v.versi} = ${v.label_versi}`;
  if (v.draf) return `#${v.versi}*`;
  if (v.tidak_dikirim) return `#${v.versi} (tidak dikirim)`;
  return `#${v.versi}`;
}

// one entry of the ACC history for the staff. v2.20: the customer's version numbers (v1.0, v1.1, ...; server
// `label_versi`) next to the upload numbers, so the CS and the customer name a version the same way.
export function riwayatTeks(h, label = {}) {
  const lv = label[String(h.versi_mockup)];
  const pada = lv ? ` pada ${lv}` : "";
  if (h.aksi === "MOCKUP") return `unggah mockup #${h.versi_mockup}${h.draf ? " (draf)" : ""}${!h.draf && lv ? ` = ${lv}` : ""}`;
  if (h.aksi === "IZIN_REVISI") return `revisi ke-${h.revisi_ke} disetujui: ${h.alasan}`;
  if (h.aksi === "REVISI_SELESAI") return `revisi ke-${h.revisi_ke} selesai${h.oleh_nama ? ` (${h.oleh_nama})` : ""}, ${lv || `mockup #${h.versi_mockup}`} dikirim ke customer`;
  if (h.aksi === "LINK_DIKIRIM") return `link ACC ${h.cara === "WA" ? "dikirim via WA" : "disalin"}${h.oleh_nama ? ` oleh ${h.oleh_nama}` : ""}`;
  if (h.status === "REVISI") return `revisi${h.revisi_ke ? ` ke-${h.revisi_ke}` : ""}${h.revisi_h1 ? " (H+1)" : ""}${pada}: ${h.catatan}`;
  if (h.status === "DISETUJUI") return `disetujui${pada}${h.sumber === "WHATSAPP" ? " (WhatsApp)" : ""}`;
  return h.status || h.aksi;
}
